import { useState } from 'react';
import { safeStorage } from '@/lib/safeStorage';
import { planPrice } from '@/config/plans';
import { formatDate } from '@/utils/dates';
import { Crown, Check, Sparkles, Calendar, Brain, Bookmark, Info, Heart, Gift, Search, Wand2, Archive, ChevronDown } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { isPremiumActive, type SubscriptionType } from '@/hooks/useSubscription';

interface ProScreenProps {
  subscriptionType: SubscriptionType;
  subscriptionExpiresAt?: string | null;
  onUpgrade: (plan: 'trip_pass' | 'monthly' | 'yearly') => Promise<boolean>;
  onTip: (amount: number) => Promise<boolean>;
  currentDayGroups?: number;
  currentLocationGroups?: number;
  maxFreeDayGroups?: number;
  maxFreeLocationGroups?: number;
  interestSearchesRemaining?: number;
  deepSearchesRemaining?: number;
  subscriptionPausedUntil?: string | null;
  userId?: string;
  referralCode?: string | null;
  userName?: string | null;
  isBeta?: boolean;
}

const PLAN_TIERS: Record<SubscriptionType, number> = {
  free: 0,
  trip_pass: 1,
  monthly: 2,
  yearly: 3,
};

export function ProScreen({
  subscriptionType,
  subscriptionExpiresAt,
  onUpgrade,
  onTip,
  currentDayGroups = 0,
  currentLocationGroups = 0,
  maxFreeDayGroups = 5,
  maxFreeLocationGroups = 3,
  interestSearchesRemaining,
  deepSearchesRemaining,
  subscriptionPausedUntil,
  userId,
  userName,
  isBeta = false,
}: ProScreenProps) {
  const { t } = useLanguage();
  const [selectedPlan, setSelectedPlan] = useState<'trip_pass' | 'monthly' | 'yearly' | 'tip'>(() => {
    // Default selection: next tier up, or tip if already yearly
    if (subscriptionType === 'yearly') return 'tip';
    if (subscriptionType === 'monthly') return 'yearly';
    return 'yearly';
  });
  const [processing, setProcessing] = useState(false);
  const [tipAmount, setTipAmount] = useState('');
  const [thankYou, setThankYou] = useState(false);
  const [whyExpanded, setWhyExpanded] = useState(() => {
    return safeStorage.getItem('tb_why_collapsed') !== '1';
  });

  const currentTier = PLAN_TIERS[subscriptionType];
  // Match App.tsx — paid type AND (notExpired OR isPaused), excluding paused-past-expiry.
  const isPremium = isPremiumActive(subscriptionType, subscriptionExpiresAt, subscriptionPausedUntil);
  const showYearly = currentTier < PLAN_TIERS.yearly;
  const showMonthly = currentTier < PLAN_TIERS.monthly;
  const showTripPass = currentTier < PLAN_TIERS.trip_pass;
  const hasUpsells = showYearly || showMonthly || showTripPass;

  const handleAction = async () => {
    if (selectedPlan === 'tip') {
      const amount = parseFloat(tipAmount);
      if (!amount || amount <= 0) return;
      setProcessing(true);
      const success = await onTip(amount);
      setProcessing(false);
      if (success) {
        setThankYou(true);
        setTipAmount('');
        setTimeout(() => setThankYou(false), 4000);
      }
      return;
    }

    setProcessing(true);
    const success = await onUpgrade(selectedPlan);
    setProcessing(false);
    if (success) {
      setThankYou(true);
      setTimeout(() => setThankYou(false), 4000);
    }
  };

  const parsedTip = parseFloat(tipAmount);
  const validTip = parsedTip > 0 && !isNaN(parsedTip);

  const buttonLabel = () => {
    if (processing) return null; // spinner shown instead
    if (selectedPlan === 'tip' && validTip) {
      return `Tip $${parsedTip.toFixed(2)} Now`;
    }
    if (selectedPlan === 'tip') {
      return t.payment?.tipNow || 'Enter amount above';
    }
    return isBeta ? 'Activate Free Beta' : (t.payment?.upgradeNow || 'Upgrade Now');
  };

  const isPaused = subscriptionPausedUntil && new Date(subscriptionPausedUntil) > new Date();
  const isIndefinitePause = isPaused && new Date(subscriptionPausedUntil!).getFullYear() >= 2099;

  // Status header text with expiry/renewal info
  const statusText = () => {
    if (isPaused) {
      const paidExpired = subscriptionExpiresAt && new Date(subscriptionExpiresAt) < new Date();
      if (isIndefinitePause) {
        if (paidExpired || !subscriptionExpiresAt) return t.settings?.pauseFrozenResume || 'Payment frozen — resume to reactivate Pro';
        const expiryDate = formatDate(subscriptionExpiresAt);
        return (t.settings?.pauseFrozenActiveUntil || 'Payment frozen — Pro active until {date}').replace('{date}', expiryDate);
      }
      const pauseEnd = formatDate(subscriptionPausedUntil!);
      if (paidExpired) return (t.settings?.pauseFrozenExpires || 'Payment frozen — Pro expires, resumes {date}').replace('{date}', pauseEnd);
      if (!subscriptionExpiresAt) return 'Payment frozen — resume to reactivate Pro';
      const expiryDate = formatDate(subscriptionExpiresAt);
      return (t.settings?.pauseFrozenActiveResumes || 'Payment frozen — Pro active until {date}, resumes {resumeDate}').replace('{date}', expiryDate).replace('{resumeDate}', pauseEnd);
    }

    if (!subscriptionExpiresAt) {
      if (isPremium) return subscriptionType === 'yearly' ? (t.settings?.yearlyActive || 'Yearly — Active') : subscriptionType === 'monthly' ? (t.settings?.monthlyActive || 'Monthly — Active') : (t.settings?.tripPassActive || 'Trip Pass — Active');
      return null;
    }

    const expires = new Date(subscriptionExpiresAt);
    const now = new Date();
    const diffMs = expires.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (subscriptionType === 'trip_pass') {
      if (diffMs <= 0) return t.settings?.tripPassExpired || 'Trip Pass — expired';
      if (diffDays === 0) return t.settings?.tripPassLastDay || 'Trip Pass — last day!';
      return (t.settings?.tripPassDaysLeft || 'Trip Pass — {count} day{s} left').replace('{count}', String(diffDays)).replace('{s}', diffDays !== 1 ? 's' : '');
    }

    const dateStr = formatDate(expires);
    if (subscriptionType === 'yearly') return (t.settings?.yearlyRenews || 'Yearly — renews {date}').replace('{date}', dateStr);
    if (subscriptionType === 'monthly') return (t.settings?.monthlyRenews || 'Monthly — renews {date}').replace('{date}', dateStr);
    return null;
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] pb-24">
      {/* Thank you toast */}
      {thankYou && (
        <div className="fixed top-[max(1rem,calc(env(safe-area-inset-top)+0.5rem))] left-1/2 -translate-x-1/2 z-[60] px-6 py-4 rounded-xl bg-gradient-to-r from-[var(--accent-gold)] to-amber-500 text-black font-bold text-base shadow-lg shadow-amber-500/30 animate-fade-in flex items-center gap-3">
          <Heart className="w-5 h-5" />
          {t.payment?.thankYou || 'Thank you so much!'}
        </div>
      )}

      <div className="max-w-lg mx-auto px-4 pt-6">
        {/* User name */}
        {userName && (
          <p className="text-lg font-semibold text-[var(--text-primary)] text-center mb-4">
            {userName}
          </p>
        )}

        {/* Why we charge — collapsible */}
        <button
          onClick={() => {
            const next = !whyExpanded;
            setWhyExpanded(next);
            safeStorage.setItem('tb_why_collapsed', next ? '0' : '1');
          }}
          className="w-full mb-5 p-4 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] text-left transition-colors hover:border-[var(--text-secondary)]/20"
        >
          <div className="flex items-center gap-2.5">
            <Info className="w-4 h-4 text-[var(--text-secondary)] flex-shrink-0" />
            <span className="text-[13px] font-medium text-[var(--text-secondary)] flex-1">{t.payment?.whyWeChargeTitle || 'Why does TripBoom have a Pro tier?'}</span>
            <ChevronDown className={`w-4 h-4 text-[var(--text-secondary)] transition-transform ${whyExpanded ? 'rotate-180' : ''}`} />
          </div>
          {whyExpanded && (
            <p className="text-[13px] leading-relaxed italic text-[var(--text-secondary)] mt-2 ml-6.5">
              {t.payment?.whyWeCharge || 'Google charges us, every time we use the basic data that\'s free in Maps. We add AI, Taste Learning, Practicality prioritisation, and more. We can only hold Google data for a few days. Sharing this app with others, and your financial support, keeps us running. And we think that\'s cool.'}
            </p>
          )}
        </button>

        {/* Status header */}
        <div className="flex items-center gap-3 mb-6">
          <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center shadow-lg ${
            isPremium
              ? 'bg-gradient-to-br from-[var(--accent-gold)] to-amber-600 shadow-amber-500/20'
              : 'bg-[var(--bg-elevated)] border border-[var(--border)]'
          }`}>
            <Crown className={`w-6 h-6 ${isPremium ? 'text-white' : 'text-[var(--text-secondary)]'}`} />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-[var(--text-primary)]">
              {isPaused
                ? subscriptionExpiresAt && new Date(subscriptionExpiresAt) < new Date()
                  ? (t.settings?.proPaused || 'Pro Paused')
                  : (t.settings?.proEnabled || 'Pro Enabled')
                : subscriptionType === 'trip_pass'
                ? (t.settings?.proEnabled || 'Pro Enabled')
                : isPremium
                ? (t.settings?.premiumSupporter || 'Pro Member')
                : (t.settings?.getProCTA || 'Get Pro')}
            </h1>
            <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">
              {statusText() || (t.settings?.proSubtitleFree || 'Unlimited searches, saves, credits & AI')}
            </p>
          </div>
        </div>

        {/* What you get / Your benefits */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-3.5 h-3.5 text-[var(--accent-gold)]" />
            <span className="text-[13px] font-medium text-[var(--text-primary)]">
              {isPremium ? (t.settings?.yourBenefits || 'Your benefits') : (t.payment?.whatYouGet || 'What you get')}
            </span>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-[13px] text-[var(--text-secondary)]">
              <Check className={`w-3 h-3 flex-shrink-0 ${isPremium ? 'text-[var(--accent-gold)]' : 'text-[var(--accent-teal)]'}`} />
              <Search className="w-3.5 h-3.5 text-[var(--accent-gold)] flex-shrink-0" />
              <span>{isPremium ? (t.settings?.unlimitedInterestSearchesFull || 'Unlimited "Interested in..." searches') : (t.settings?.unlimitedInterestSearches || 'Unlimited interest searches')}</span>
            </div>
            <div className="flex items-center gap-2 text-[13px] text-[var(--text-secondary)]">
              <Check className={`w-3 h-3 flex-shrink-0 ${isPremium ? 'text-[var(--accent-gold)]' : 'text-[var(--accent-teal)]'}`} />
              <Wand2 className="w-3.5 h-3.5 text-[var(--accent-gold)] flex-shrink-0" />
              <span>{t.settings?.savedInterestsBenefit || 'Saved Interests — personalise every search'}</span>
            </div>
            <div className="flex items-center gap-2 text-[13px] text-[var(--text-secondary)]">
              <Check className={`w-3 h-3 flex-shrink-0 ${isPremium ? 'text-[var(--accent-gold)]' : 'text-[var(--accent-teal)]'}`} />
              <Brain className="w-3.5 h-3.5 text-[var(--accent-gold)] flex-shrink-0" />
              <span>{t.payment?.aiHighMode || 'AI High mode — deepest analysis'}</span>
            </div>
            <div className="flex items-center gap-2 text-[13px] text-[var(--text-secondary)]">
              <Check className={`w-3 h-3 flex-shrink-0 ${isPremium ? 'text-[var(--accent-gold)]' : 'text-[var(--accent-teal)]'}`} />
              <Calendar className="w-3.5 h-3.5 text-[var(--accent-gold)] flex-shrink-0" />
              <span>{t.settings?.unlimitedGroups || 'Unlimited day/location groups'}</span>
            </div>
            <div className="flex items-center gap-2 text-[13px] text-[var(--text-secondary)]">
              <Check className={`w-3 h-3 flex-shrink-0 ${isPremium ? 'text-[var(--accent-gold)]' : 'text-[var(--accent-teal)]'}`} />
              <Bookmark className="w-3.5 h-3.5 text-[var(--accent-gold)] flex-shrink-0" />
              <span>{t.payment?.saveUnlimitedTrips || 'Save unlimited trips forever'}</span>
            </div>
            <div className="flex items-center gap-2 text-[13px] text-[var(--text-secondary)]">
              <Check className={`w-3 h-3 flex-shrink-0 ${isPremium ? 'text-[var(--accent-gold)]' : 'text-[var(--accent-teal)]'}`} />
              <Archive className="w-3.5 h-3.5 text-[var(--accent-gold)] flex-shrink-0" />
              <span>{t.settings?.unlockArchived || 'Unlock Time Archived Trips'}</span>
            </div>
          </div>

          {/* Credit status */}
          <div className="mt-3 p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border)] space-y-1.5">
              <p className="text-[11px] font-medium text-[var(--text-secondary)] uppercase tracking-wide">
                {isPremium ? (t.settings?.yourCredits || 'Your credits') : !userId ? (t.settings?.freeCreditsOnReg || 'Your free credits — on registration') : (t.settings?.freeCredits || 'Your free credits')}
              </p>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[var(--text-secondary)]">{t.settings?.dayGroups || 'Day Groups'}</span>
                {isPremium ? (
                  <span className="font-semibold text-[var(--accent-gold)]">{t.settings?.unlimited || 'Unlimited'}</span>
                ) : !userId ? (
                  <span className="font-semibold text-[var(--accent-teal)]">{maxFreeDayGroups}</span>
                ) : (
                  <span className={`font-semibold ${currentDayGroups >= maxFreeDayGroups ? 'text-[var(--status-red)]' : 'text-[var(--accent-teal)]'}`}>
                    {currentDayGroups}/{maxFreeDayGroups}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[var(--text-secondary)]">{t.settings?.locationGroups || 'Location Groups'}</span>
                {isPremium ? (
                  <span className="font-semibold text-[var(--accent-gold)]">{t.settings?.unlimited || 'Unlimited'}</span>
                ) : !userId ? (
                  <span className="font-semibold text-[var(--accent-teal)]">{maxFreeLocationGroups}</span>
                ) : (
                  <span className={`font-semibold ${currentLocationGroups >= maxFreeLocationGroups ? 'text-[var(--status-red)]' : 'text-[var(--accent-teal)]'}`}>
                    {currentLocationGroups}/{maxFreeLocationGroups}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[var(--text-secondary)]">{t.settings?.interestSearches || 'Interest Searches'}</span>
                {isPremium ? (
                  <span className="font-semibold text-[var(--accent-gold)]">{t.settings?.unlimited || 'Unlimited'}</span>
                ) : !userId ? (
                  <span className="font-semibold text-[var(--accent-teal)]">2/month</span>
                ) : interestSearchesRemaining !== undefined ? (
                  <span className={`font-semibold ${interestSearchesRemaining > 3 ? 'text-[var(--accent-teal)]' : interestSearchesRemaining > 0 ? 'text-[var(--accent-gold)]' : 'text-[var(--status-red)]'}`}>
                    {interestSearchesRemaining}
                  </span>
                ) : null}
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[var(--text-secondary)]">{t.settings?.highAiSearches || 'High AI Trip Searches'}</span>
                {isPremium ? (
                  <span className="font-semibold text-[var(--accent-gold)]">{t.settings?.unlimited || 'Unlimited'}</span>
                ) : !userId ? (
                  <span className="font-semibold text-[var(--accent-teal)]">5</span>
                ) : deepSearchesRemaining !== undefined ? (
                  <span className={`font-semibold ${deepSearchesRemaining > 0 ? 'text-[var(--accent-teal)]' : 'text-[var(--status-red)]'}`}>
                    {deepSearchesRemaining}
                  </span>
                ) : null}
              </div>
            </div>
        </div>

        {/* Beta banner */}
        {isBeta && (
          <div className="mb-4 p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-center">
            <p className="text-sm font-semibold text-indigo-400">Beta Access — All Pro features are free</p>
            <p className="text-xs text-indigo-400/70 mt-0.5">You will not be charged. Activate to unlock everything.</p>
          </div>
        )}

        {/* Contextual upsell nudge */}
        {hasUpsells && isPremium && (
          <p className="text-[13px] text-[var(--text-secondary)] text-center mb-3 italic">
            {subscriptionType === 'trip_pass'
              ? (t.settings?.upsellFromTripPass || 'Save with a subscription for unlimited credits on every trip')
              : subscriptionType === 'monthly'
              ? (t.settings?.upsellFromMonthly || 'Switch to Yearly and save over 50%')
              : null}
          </p>
        )}

        {/* Plan selection — only upsells */}
        {hasUpsells && (
          <div className="space-y-2 mb-4">
            {showYearly && (
              <button
                onClick={() => setSelectedPlan('yearly')}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left relative overflow-hidden ${
                  selectedPlan === 'yearly'
                    ? 'border-[var(--accent-gold)] bg-[var(--accent-gold)]/10'
                    : 'border-[var(--border)] hover:border-[var(--accent-gold)]/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-[var(--text-primary)]">{t.payment?.yearly || 'Yearly'}</p>
                    <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">
                      {t.payment?.billedAnnuallyNeutral || 'Best for frequent travellers'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[var(--accent-gold)]">{planPrice('yearly')}</p>
                    <p className="text-xs text-[var(--text-secondary)]">{t.payment?.perYear || '/year'}</p>
                  </div>
                </div>
              </button>
            )}

            {showMonthly && (
              <button
                onClick={() => setSelectedPlan('monthly')}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  selectedPlan === 'monthly'
                    ? 'border-[var(--accent-teal)] bg-[var(--accent-teal)]/10'
                    : 'border-[var(--border)] hover:border-[var(--accent-teal)]/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-[var(--text-primary)]">{t.payment?.monthly || 'Monthly'}</p>
                    <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">
                      {t.payment?.billedMonthlyNeutral || 'Flexible — cancel any time'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[var(--accent-teal)]">{planPrice('monthly')}</p>
                    <p className="text-xs text-[var(--text-secondary)]">{t.payment?.perMonth || '/month'}</p>
                  </div>
                </div>
              </button>
            )}

            {showTripPass && (
              <button
                onClick={() => setSelectedPlan('trip_pass')}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  selectedPlan === 'trip_pass'
                    ? 'border-[var(--accent-teal)] bg-[var(--accent-teal)]/10'
                    : 'border-[var(--border)] hover:border-[var(--accent-teal)]/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-[var(--text-primary)]">{t.payment?.tripPass || 'Trip Pass'}</p>
                    <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">
                      {t.payment?.tripPassDescription || '7 days of premium — buy when you travel'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[var(--accent-teal)]">{planPrice('trip_pass')}</p>
                    <p className="text-xs text-[var(--text-secondary)]">{t.payment?.oneOff || 'one-off'}</p>
                  </div>
                </div>
              </button>
            )}
          </div>
        )}

        {/* Custom Tip */}
        <button
          onClick={() => setSelectedPlan('tip')}
          className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
            selectedPlan === 'tip'
              ? 'border-[var(--accent-teal)] bg-[var(--accent-teal)]/10'
              : 'border-[var(--border)] hover:border-[var(--accent-teal)]/50'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Gift className="w-4 h-4 text-[var(--accent-teal)]" />
              <div>
                <p className="font-semibold text-[var(--text-primary)]">{t.payment?.customTip || 'Tip the Concierge'}</p>
                <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">
                  {t.payment?.customTipDescription || 'A donation to help us build more'}
                </p>
              </div>
            </div>
            <Heart className="w-4 h-4 text-[var(--accent-teal)]" />
          </div>
        </button>

        {/* Tip amount input */}
        {selectedPlan === 'tip' && (
          <div className="mt-3 flex items-center gap-3">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg font-bold text-[var(--text-secondary)]">$</span>
              <input
                type="number"
                min="0.50"
                step="0.50"
                value={tipAmount}
                onChange={(e) => setTipAmount(e.target.value)}
                placeholder="5.00"
                className="w-full pl-8 pr-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] text-lg font-bold text-[var(--text-primary)] placeholder:text-[var(--text-secondary)]/40 focus:outline-none focus:border-[var(--accent-teal)] transition-colors"
              />
            </div>
            <div className="flex gap-1.5">
              {[2, 5, 10].map((amt) => (
                <button
                  key={amt}
                  onClick={() => setTipAmount(String(amt))}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    tipAmount === String(amt)
                      ? 'bg-[var(--accent-teal)] text-white'
                      : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:bg-[var(--accent-teal)]/10'
                  }`}
                >
                  ${amt}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Action button */}
        <button
          onClick={handleAction}
          disabled={processing || (selectedPlan === 'tip' && !validTip)}
          className="w-full mt-4 py-4 rounded-xl font-semibold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-[var(--accent-gold)] to-amber-500 text-black hover:shadow-lg hover:shadow-amber-500/25 hover:-translate-y-0.5 active:translate-y-0"
        >
          {processing ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              {t.payment?.processing || 'Processing...'}
            </span>
          ) : (
            buttonLabel()
          )}
        </button>

        <p className="text-xs text-center text-[var(--text-secondary)]/60 mt-3">
          {t.payment?.securePayment || 'Secure checkout'}
        </p>

        <a
          href="https://www.tripboom.app/reviews"
          target="_blank"
          rel="noopener noreferrer"
          className="block text-xs text-center text-[var(--accent-teal)] hover:text-[var(--accent-teal)]/80 pt-3 pb-1 transition-colors"
        >
          {t.settings?.feedbackViaWebsite || 'Feedback via Website ↗'}
        </a>
      </div>
    </div>
  );
}
